import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface OnboardingProgress {
  profile_completed: boolean;
  directory_explored: boolean;
  first_message_sent: boolean;
  first_opportunity_viewed: boolean;
  completed_at: string | null;
}

export const useOnboardingProgress = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['onboarding', user?.id],
    queryFn: async (): Promise<OnboardingProgress | null> => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('onboarding_progress')
        .select('profile_completed, directory_explored, first_message_sent, first_opportunity_viewed, completed_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('[useOnboardingProgress] Error:', error);
        return null;
      }

      // Create if not exists
      if (!data) {
        const { data: newData } = await supabase
          .from('onboarding_progress')
          .insert({ user_id: user.id })
          .select('profile_completed, directory_explored, first_message_sent, first_opportunity_viewed, completed_at')
          .single();
        return newData as OnboardingProgress | null;
      }

      return data as OnboardingProgress;
    },
    enabled: !!user,
    staleTime: 30_000,
  });

  const markStep = useMutation({
    mutationFn: async (field: keyof Omit<OnboardingProgress, 'completed_at'>) => {
      if (!user) return;

      const update: Record<string, unknown> = {
        [field]: true,
        updated_at: new Date().toISOString(),
      };

      // Check if all steps would be complete
      const current = query.data;
      if (current) {
        const allComplete = Object.entries({ ...current, [field]: true })
          .filter(([k]) => k !== 'completed_at')
          .every(([, v]) => v === true);
        
        if (allComplete) {
          update.completed_at = new Date().toISOString();
        }
      }

      const { error } = await supabase
        .from('onboarding_progress')
        .update(update)
        .eq('user_id', user.id);

      if (error) console.error('[useOnboardingProgress] Mark error:', error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', user?.id] });
    },
  });

  return {
    progress: query.data,
    isLoading: query.isLoading,
    markStep: markStep.mutate,
    isComplete: !!query.data?.completed_at,
  };
};
